import React, { useState, useEffect } from 'react';
import { Row } from 'reactstrap';
import BusinessAdminTemplate from '../UserAccess/BusinessAdminTemplate';
import QueryView from '../Common/QueryView';
import KaAlert from '../Common/KaAlert';
import SpinnerButton from '../Common/SpinnerButton';
import useDisplayMessage, { UseDisplayMessage } from '../Common/useDisplayMessage';
import { KaSelect } from '../Common/KaSelect';
import FilterInput from '../FilterInput';
import { useSelectedBusiness } from '../UserAccess/useSelectedBusiness';
import useGetSharedProductConnections, {
  SiteConnectionsDto,
  SourceConnectionDto,
  SharedProductItemDto,
} from '../../requests/useGetSharedProductConnections';
import useUpdateProductMappings from '../../requests/useUpdateProductMappings';
import { ProductSelectionModal } from './ProductSelectionModal';
import Confirm from '../Common/Confirm';

interface MappingState {
  [connectionId: string]: {
    [productId: string]: string[];
  };
}

interface ModalState {
  connectionId: string;
  productId: string;
  productName: string;
  sourceBusinessName: string;
  sourceProducts: SharedProductItemDto[];
}

interface SharedProductsContentProps {
  siteConnections: SiteConnectionsDto;
  filter: string;
  onHasUnsavedChanges: (hasChanges: boolean) => void;
  displayMessage: UseDisplayMessage;
  onRefreshData: () => void;
}

const SharedProductsContent = ({ siteConnections, filter, onHasUnsavedChanges, onRefreshData, displayMessage }: SharedProductsContentProps) => {
  const business = useSelectedBusiness();
  const [mappingState, setMappingState] = useState<MappingState>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [modalState, setModalState] = useState<ModalState | undefined>(undefined);

  const updateMappings = useUpdateProductMappings({
    onSuccess: () => {
      displayMessage.success('Product mappings saved successfully');
      setHasUnsavedChanges(false);
      onRefreshData();
    },
    onError: () => displayMessage.fail('Failed to save product mappings'),
  });

  useEffect(() => {
    const initialMappings: MappingState = {};
    siteConnections.sourceConnections.forEach(source => {
      initialMappings[source.connectionId] = {};
      source.mappings.forEach(mapping => {
        initialMappings[source.connectionId][mapping.productId] = mapping.sourceProductIds || [];
      });
    });
    setMappingState(initialMappings);
    setHasUnsavedChanges(false);
  }, [siteConnections]);

  useEffect(() => {
    onHasUnsavedChanges(hasUnsavedChanges);
  }, [hasUnsavedChanges, onHasUnsavedChanges]);

  const handleOpenModal = (product: SharedProductItemDto, source: SourceConnectionDto) => {
    setModalState({
      connectionId: source.connectionId,
      productId: product.id,
      productName: product.name,
      sourceBusinessName: source.sourceBusinessName,
      sourceProducts: source.products
    });
  };

  const handleSelectProduct = (sourceProductId: string) => {
    if (!modalState) return;

    setMappingState(prev => {
      const connectionMappings = prev[modalState.connectionId] || {};
      const currentMappings = connectionMappings[modalState.productId] || [];
      return {
        ...prev,
        [modalState.connectionId]: {
          ...connectionMappings,
          [modalState.productId]: [...currentMappings, sourceProductId]
        }
      };
    });
    setHasUnsavedChanges(true);
    setModalState(undefined);
  };

  const handleRemoveMapping = (connectionId: string, productId: string, sourceProductId: string) => {
    setMappingState(prev => {
      const connectionMappings = prev[connectionId] || {};
      const currentMappings = connectionMappings[productId] || [];
      const updatedMappings = currentMappings.filter(id => id !== sourceProductId);

      const newConnectionMappings = { ...connectionMappings };
      newConnectionMappings[productId] = updatedMappings;

      return {
        ...prev,
        [connectionId]: newConnectionMappings
      };
    });
    setHasUnsavedChanges(true);
  };

  const handleSave = () => {
    const allMappingsRequests = siteConnections.sourceConnections.map(connection => {
      const connectionMappings = mappingState[connection.connectionId] || {};
      return {
        connectionId: connection.connectionId,
        mappings: siteConnections.products.map(product => ({
          productId: product.id,
          sourceProductIds: connectionMappings[product.id] || [],
        })),
      };
    });

    updateMappings.request(allMappingsRequests);
  };

  const filteredProducts = siteConnections.products
    .filter(product => {
      const searchTerm = filter.toLowerCase();

      if (product.name.toLowerCase().includes(searchTerm)) {
        return true;
      }

      for (const source of siteConnections.sourceConnections) {
        const connectionMappings = mappingState[source.connectionId] || {};
        const mappedSourceProductIds = connectionMappings[product.id] || [];
        const mappedProducts = source.products.filter(p => mappedSourceProductIds.includes(p.id));

        if (mappedProducts.some(p => p.name.toLowerCase().includes(searchTerm))) {
          return true;
        }
      }

      return false;
    })
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

  const sortedSourceConnections = [...siteConnections.sourceConnections]
    .sort((a, b) => a.sourceBusinessName.toLowerCase().localeCompare(b.sourceBusinessName.toLowerCase()));

  return <>
    <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
    {siteConnections.products.length === 0 ? (
      <div className="text-center py-5">
        <h4>No Products Assigned</h4>
        <p className="text-muted">This site currently has no products assigned. Please assign products to this site before managing product mappings.</p>
      </div>
    ) : (
      <table className="table table-bordered table-responsive w-auto">
        <thead>
          <tr className='position-sticky zi-1 bg-white navbar-offset'>
            <th className="align-middle text-center bg-soft-secondary" style={{ width: '250px' }}>
              <div className="fw-bold">{business.name} Products</div>
            </th>
            {sortedSourceConnections.map(source => (
              <th key={source.connectionId} className="align-middle text-center bg-soft-secondary" style={{ width: '320px' }}>
                <div className="fw-bold">{source.sourceBusinessName} Products</div>
                <small className="text-muted d-block">{source.sourceSiteName}</small>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filteredProducts.map(product => (
            <tr key={product.id}>
              <td className="align-middle bg-soft-secondary py-2">
                <div className="fw-semibold">
                  {product.name}
                </div>
              </td>
              {sortedSourceConnections.map(source => {
                const connectionMappings = mappingState[source.connectionId] || {};
                const mappedSourceProductIds = connectionMappings[product.id] || [];
                const mappedProducts = source.products.filter(p => mappedSourceProductIds.includes(p.id));

                return (
                  <td key={source.connectionId} className="align-top py-2" style={{ maxWidth: '245px' }}>
                    <div className="d-flex flex-column gap-2">
                      {mappedProducts.map(mappedProduct => (
                        <div key={mappedProduct.id} className="border rounded position-relative p-2">
                          <div className="text-truncate pe-5">{mappedProduct.name}</div>
                          <button
                            className="position-absolute top-0 end-0 btn btn-sm btn-link text-danger p-0 pt-1 m-1"
                            onClick={() => handleRemoveMapping(source.connectionId, product.id, mappedProduct.id)}
                          >
                            <i className="bi bi-x-circle"></i>
                          </button>
                        </div>
                      ))}
                      {source.products.length > 0 && (
                        <div
                          className="border rounded position-relative p-2 text-muted"
                          role="button"
                          onClick={() => handleOpenModal(product, source)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div>Add Product</div>
                          <div className="position-absolute top-0 end-0 text-primary p-0 pt-1 m-1">
                            <i className="bi bi-plus-circle"></i>
                          </div>
                        </div>
                      )}
                      {source.products.length === 0 && mappedProducts.length === 0 && (
                        <div className="text-center text-muted">
                          <small>Source site has no products available for mapping</small>
                        </div>
                      )}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    )}

    {modalState && (
      <ProductSelectionModal
        onClose={() => setModalState(undefined)}
        onSelect={handleSelectProduct}
        products={modalState.sourceProducts}
        siteProducts={siteConnections.products}
        productName={modalState.productName}
        sourceBusinessName={modalState.sourceBusinessName}
        existingMappings={mappingState[modalState.connectionId] || {}}
      />
    )}

    <div className="position-fixed bottom-0 start-0 end-0 bg-white border-top p-2 text-end">
      <SpinnerButton
        className="btn-ghost-primary"
        onClick={handleSave}
        spinning={updateMappings.isLoading}>
        Save Mappings
      </SpinnerButton>
    </div>
  </>
};

const SharedProductsPage = () => {
  const displayMessage = useDisplayMessage();
  const [selectedSiteId, setSelectedSiteId] = useState<string | undefined>(undefined);
  const [filterText, setFilterText] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingSiteChange, setPendingSiteChange] = useState<string | undefined>(undefined);

  const getConnections = useGetSharedProductConnections();
  const connections = React.useMemo(
    () => Array.isArray(getConnections.data) ? getConnections.data : [],
    [getConnections.data]
  );

  useEffect(() => {
    if (connections.length === 1 && !selectedSiteId) {
      setSelectedSiteId(selectedSiteId);
    }
  }, [connections]); // Remove selectedSite from deps to avoid infinite loop

  const connectionOptions = connections
    .map(conn => ({
      value: conn.siteId,
      label: conn.siteName
    }))
    .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));

  const handleConnectionChange = (siteId: string) => {
    if (hasUnsavedChanges) {
      setPendingSiteChange(siteId);
    } else {
      setSelectedSiteId(connections.find(s => s.siteId === siteId)?.siteId);
    }
  };

  const confirmSiteChange = () => {
    if (pendingSiteChange) {
      setSelectedSiteId(connections.find(s => s.siteId === pendingSiteChange)?.siteId);
      setHasUnsavedChanges(false);
      setPendingSiteChange(undefined);
    }
  };

  const selectedSite = connections.find(s => s.siteId === selectedSiteId);

  return <BusinessAdminTemplate>
    <Confirm
      visible={!!pendingSiteChange}
      title={`Unsaved Changes for ${selectedSite?.siteName}`}
      body={`You have unsaved changes. Please save your work before selecting another location, or you will lose any updates you have made.
               Are you sure that you want to change site?`}
      isLoading={false}
      onDismiss={() => setPendingSiteChange(undefined)}
      onConfirm={confirmSiteChange} />
    <QueryView
      query={getConnections}
      renderData={() => <>
        <Row className="justify-content-between mb-2">
          <h2 className="col-auto ka-blue">Shared Products</h2>
        </Row>
        <form>
          <div className="row gx-3 mb-3">
            <div className="col-4">
              <FilterInput
                placeholder='Filter products by name'
                size='sm'
                value={filterText}
                onFilterTextChange={setFilterText}
              />
            </div>
            <div className="col-4 zi-3">
              <KaSelect
                options={connectionOptions}
                value={connectionOptions.find(opt => opt.value === selectedSite?.siteId)}
                onChange={(option) => {
                  if (option && 'value' in option) {
                    handleConnectionChange(option.value);
                  }
                }}
                placeholder="Select Site"
                isClearable={false}
                isSearchable={false}
              />
            </div>
          </div>
        </form>
        {selectedSite
          ? <SharedProductsContent
            siteConnections={selectedSite}
            filter={filterText}
            onHasUnsavedChanges={setHasUnsavedChanges}
            displayMessage={displayMessage}
            onRefreshData={() => getConnections.query()} />
          : <div className="text-center py-5">
            <h4>Select a Site</h4>
            <p className="text-muted">Choose a site from the dropdown above to manage product mappings.</p>
          </div>
        }
      </>}
    />
  </BusinessAdminTemplate>;
};

export { SharedProductsPage };